import React from "react"
import {
    Button, Checkbox, Container, Grid, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tooltip,
} from "@material-ui/core"
import { Block, DeleteForever, Schedule } from "@material-ui/icons"
import { Pagination } from "@material-ui/lab"
import paginationStyles from '../AgentsComponent/styles.module.css'
import styles from './styles.module.css'
import { Pages } from "../../models/Pages"
import { BlockedNumber } from "../../models/RobocallBlocks/BlockedNumber"
import { BusinessesSample, EvidenceSummary, ScoreBadge, StatusBadge, VetoBadge } from "./Badges"

interface IProps {
    blocks: BlockedNumber[]
    pagination: Pages | null
    selected: number[]
    minSamples: number
    handleChangePage: (page: number) => void
    onToggleSelect: (id: number) => void
    onToggleSelectAll: () => void
    onConfirm: (block: BlockedNumber) => void
    onClear: (block: BlockedNumber) => void
    onExpiry: (block: BlockedNumber) => void
}

/**
 * The review queue table.
 *
 * Everything a reviewer needs is on the row — §8a's requirement is that a decision can be
 * made without running a query, so the evidence is rendered inline rather than hidden
 * behind a drill-down.
 *
 * Selection is scoped to the rows currently rendered (i.e. the visible, filtered page), so
 * a bulk action can never reach a row the reviewer cannot see.
 */
const BlocksList = (props: IProps) => {

    const selectable = props.blocks
        .filter((b) => b.status === 'candidate' || b.status === 'active')
        .map((b) => b.id!)

    const allSelected = selectable.length > 0
        && selectable.every((id) => props.selected.indexOf(id) !== -1)

    return (
        <Container maxWidth={false}>
            <Grid item xs={12}>
                <TableContainer component={Paper}>
                    <Table aria-label="robocall blocks" size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Tooltip title="Select every actionable row on this page">
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={!allSelected && props.selected.length > 0}
                                            disabled={selectable.length === 0}
                                            onChange={props.onToggleSelectAll}
                                        />
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="left">Number</TableCell>
                                <TableCell align="center">Score</TableCell>
                                <TableCell align="center">State</TableCell>
                                <TableCell align="left">Evidence</TableCell>
                                <TableCell align="left">Businesses hit</TableCell>
                                <TableCell align="center">Hits</TableCell>
                                <TableCell align="left">Expires</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.blocks.length === 0 ?
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        <div className={styles.Empty}>
                                            Nothing matches these filters.
                                        </div>
                                    </TableCell>
                                </TableRow>
                                : null}

                            {props.blocks.map((row) => {
                                const actionable = row.status === 'candidate' || row.status === 'active'
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={props.selected.indexOf(row.id!) !== -1}
                                                disabled={!actionable}
                                                onChange={() => props.onToggleSelect(row.id!)}
                                            />
                                        </TableCell>

                                        <TableCell align="left">
                                            <a
                                                className={styles.PhoneLink}
                                                href={"/robocall-block/" + row.phone_e164}
                                            >
                                                {row.phone_e164}
                                            </a>
                                            <div className={styles.EvidenceMuted}>
                                                {row.source === 'agent' ? 'flagged by an agent' : row.source}
                                                {row.block_count > 1 ? ` · blocked ${row.block_count}×` : ''}
                                            </div>
                                        </TableCell>

                                        <TableCell align="center">
                                            <ScoreBadge score={row.score} />
                                        </TableCell>

                                        <TableCell align="center">
                                            <StatusBadge status={row.status} />
                                            <div><VetoBadge number={row} /></div>
                                        </TableCell>

                                        <TableCell align="left">
                                            <EvidenceSummary
                                                evidence={row.evidence}
                                                minSamples={props.minSamples}
                                            />
                                        </TableCell>

                                        <TableCell align="left">
                                            <BusinessesSample evidence={row.evidence} />
                                        </TableCell>

                                        <TableCell align="center">
                                            {row.hit_count}
                                            {row.last_hit_at ?
                                                <div className={styles.EvidenceMuted}>{row.last_hit_at}</div>
                                                : null}
                                        </TableCell>

                                        <TableCell align="left">
                                            <div className={styles.Evidence}>
                                                {row.status === 'active'
                                                    ? (row.expires_at || 'never')
                                                    : '—'}
                                            </div>
                                        </TableCell>

                                        <TableCell align="right">
                                            {row.status === 'candidate' ?
                                                <Tooltip title="Confirm this is a robocall and start blocking it">
                                                    <Button color="primary" onClick={() => props.onConfirm(row)}>
                                                        <Block />
                                                    </Button>
                                                </Tooltip>
                                                : null}

                                            {row.status === 'active' ?
                                                <Tooltip title="Extend or shorten the block">
                                                    <Button color="default" onClick={() => props.onExpiry(row)}>
                                                        <Schedule />
                                                    </Button>
                                                </Tooltip>
                                                : null}

                                            {actionable ?
                                                <Tooltip title={row.status === 'active'
                                                    ? 'Unblock — stops rejecting and prevents re-blocking'
                                                    : 'Not a robocall — clears it permanently'}>
                                                    <Button color="secondary" onClick={() => props.onClear(row)}>
                                                        <DeleteForever />
                                                    </Button>
                                                </Tooltip>
                                                : null}
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {(props.pagination !== null) ?
                    <Grid item xs={12}>
                        <Pagination
                            className={paginationStyles.Pagination}
                            count={props.pagination.LastPage}
                            page={props.pagination.CurrentPage}
                            onChange={(event: React.ChangeEvent<unknown> | null, newPage: number) =>
                                props.handleChangePage(newPage)}
                            variant="outlined"
                            shape="rounded"
                        />
                    </Grid>
                    : null}
            </Grid>
        </Container>
    )
}

export default BlocksList
